import type { ReactNode } from 'react'
import { alpha } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined'

import { tokens } from '../theme'

/**
 * Standardised empty-state block used across every page.
 *
 * Deliberately tight — a page with sparse data should read as "quiet",
 * not "broken". Vertical rhythm matches the surrounding cards; the icon
 * carries the visual weight so the copy can stay short.
 *
 *   <EmptyState title="No exceptions" hint="Quiet shift — nothing to act on." />
 *
 * Pass `size="sm"` inside a small card (e.g. a leaderboard). Pass
 * `size="lg"` when it's the whole page's canvas.
 */
export function EmptyState({
  title,
  hint,
  icon,
  action,
  size = 'md',
}: {
  title: string
  hint?: ReactNode
  icon?: ReactNode
  action?: ReactNode
  size?: 'sm' | 'md' | 'lg'
}) {
  const pad = size === 'sm' ? { py: 3, px: 2 } : size === 'lg' ? { py: 7, px: 3 } : { py: 5, px: 3 }
  const iconSize = size === 'sm' ? 22 : size === 'lg' ? 40 : 30
  return (
    <Stack
      spacing={size === 'sm' ? 0.75 : 1.25}
      sx={{ ...pad, textAlign: 'center', color: 'text.secondary', alignItems: 'center', justifyContent: 'center' }}
    >
      <Box
        sx={{
          width: iconSize + 20,
          height: iconSize + 20,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          bgcolor: alpha(tokens.indigo, 0.06),
          color: tokens.indigo,
          '& svg': { fontSize: iconSize },
        }}
      >
        {icon ?? <InboxOutlinedIcon />}
      </Box>
      <Typography
        variant={size === 'sm' ? 'body2' : 'body1'}
        sx={{ fontWeight: 600, color: 'text.primary' }}
      >
        {title}
      </Typography>
      {hint && (
        <Typography variant="body2" sx={{ maxWidth: 360, color: 'text.secondary' }}>
          {hint}
        </Typography>
      )}
      {action && <Box sx={{ mt: 0.5 }}>{action}</Box>}
    </Stack>
  )
}
